/**
 * Transcript dev logging — gated by localStorage (off by default).
 *
 * Enable in DevTools console, then reload:
 *   localStorage.setItem('sonnant.transcriptChunk.debug', '1')
 *
 * Disable:
 *   localStorage.removeItem('sonnant.transcriptChunk.debug')
 *
 * Or use: window.sonnantChunkDebug.enable() / .disable() / .status()
 *
 * Log prefixes (all use the same gate):
 *   [chunk-perf]   — DWCM / chunk window lifecycle (`chunkPerfLog`)
 *   [chunk-data]   — chunk data warnings/errors (`chunkDataLog`, …)
 *   [visibility]   — SPP-654 row paint registry (`visibilityLog`)
 *   [line-play]    — caption-row play/pause gate (`logTranscriptLinePlayDiagnosis`)
 *
 * Transcript line play blockers (always logs):
 *   diagnoseTranscriptLinePlay()
 *
 * Remove this module when transcript dev logging is no longer needed.
 */

export const CHUNK_DEBUG_STORAGE_KEY = 'sonnant.transcriptChunk.debug';

const isTruthy = (raw: string | null): boolean => {
  if (!raw) return false;
  const v = raw.trim().toLowerCase();
  return v === '1' || v === 'true' || v === 'yes' || v === 'on';
};

export const isChunkDebugEnabled = (): boolean => {
  if (typeof localStorage === 'undefined') return false;
  return isTruthy(localStorage.getItem(CHUNK_DEBUG_STORAGE_KEY));
};

/** Wall-clock stamp for dev console (HH:MM:SS.mmm). */
export const formatChunkLogWallTime = (date: Date = new Date()): string => {
  const h = String(date.getHours()).padStart(2, '0');
  const m = String(date.getMinutes()).padStart(2, '0');
  const s = String(date.getSeconds()).padStart(2, '0');
  const ms = String(date.getMilliseconds()).padStart(3, '0');
  return `${h}:${m}:${s}.${ms}`;
};

/** Media seconds → HH:MM:SS for readable chunk-perf logs. */
export const convertTime = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds < 0) return '00:00:00';
  const totalSec = Math.floor(seconds);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
};

const enrichChunkPerfDetail = (
  detail: Record<string, unknown>,
): Record<string, unknown> => {
  const enriched = { ...detail };
  if (typeof enriched.time === 'number' && Number.isFinite(enriched.time)) {
    enriched.timeReadable = convertTime(enriched.time);
  }
  return enriched;
};

/** `[chunk-perf]` timing / window lifecycle logs. */
export const chunkPerfLog = (
  phase: string,
  detail: Record<string, unknown> = {},
) => {
  if (!isChunkDebugEnabled()) return;
  const at = formatChunkLogWallTime();
  const t = Number(performance.now().toFixed(1));
  // eslint-disable-next-line no-console
  console.info(`[chunk-perf] ${phase}`, {
    at,
    t,
    ...enrichChunkPerfDetail(detail),
  });
};

let seekPerfStartedAt: number | null = null;

/** Start elapsed-time tracking for a progress-bar seek (release, not drag preview). */
export const beginChunkSeekPerf = (detail: Record<string, unknown> = {}) => {
  if (!isChunkDebugEnabled()) return;
  seekPerfStartedAt = performance.now();
  chunkPerfLog('seek:start', detail);
};

/** Progress-bar seek phase with elapsed ms since `beginChunkSeekPerf`. */
export const chunkSeekPerf = (
  phase: string,
  detail: Record<string, unknown> = {},
) => {
  if (!isChunkDebugEnabled()) return;
  const elapsedMs =
    seekPerfStartedAt != null
      ? Number((performance.now() - seekPerfStartedAt).toFixed(1))
      : undefined;
  chunkPerfLog(`seek:${phase}`, {
    ...enrichChunkPerfDetail(detail),
    ...(elapsedMs != null ? { elapsedMs } : {}),
  });
};

/** Mark seek complete and clear the seek perf timer. */
export const endChunkSeekPerf = (detail: Record<string, unknown> = {}) => {
  chunkSeekPerf('done', detail);
  seekPerfStartedAt = null;
};

export const cancelChunkSeekPerf = () => {
  seekPerfStartedAt = null;
};

/** `[chunk-data]` console.error (e.g. rejected cross-chunk write). */
export const chunkDataError = (...args: unknown[]) => {
  if (!isChunkDebugEnabled()) return;
  // eslint-disable-next-line no-console
  console.error(...args);
};

/** `[chunk-data]` console.warn. */
export const chunkDataWarn = (...args: unknown[]) => {
  if (!isChunkDebugEnabled()) return;
  // eslint-disable-next-line no-console
  console.warn(...args);
};

/** `[chunk-data]` console.log / group helpers. */
export const chunkDataLog = (...args: unknown[]) => {
  if (!isChunkDebugEnabled()) return;
  // eslint-disable-next-line no-console
  console.log(...args);
};

export const chunkDataGroup = (
  label: string,
  fn: () => void,
  collapsed = true,
) => {
  if (!isChunkDebugEnabled()) return;
  // eslint-disable-next-line no-console
  const group = collapsed ? console.groupCollapsed : console.group;
  group.call(console, label);
  try {
    fn();
  } finally {
    // eslint-disable-next-line no-console
    console.groupEnd();
  }
};

/** `[visibility]` SPP-654 row paint / pin / IO registry logs. */
export const visibilityLog = (
  label: string,
  detail: Record<string, unknown> = {},
) => {
  if (!isChunkDebugEnabled()) return;
  // eslint-disable-next-line no-console
  console.log(`[visibility] ${label}`, detail);
};

/** `[line-play]` transcript caption-row play/pause gate diagnostics. */
export const logTranscriptLinePlayDiagnosis = (
  phase: string,
  diagnosis: Record<string, unknown>,
  extra: Record<string, unknown> = {},
  force = false,
) => {
  if (!force && !isChunkDebugEnabled()) return;
  // eslint-disable-next-line no-console
  console.info(`[line-play] ${phase}`, {
    at: formatChunkLogWallTime(),
    t: Number(performance.now().toFixed(1)),
    ...diagnosis,
    ...extra,
  });
};

declare global {
  interface Window {
    sonnantChunkDebug?: {
      enable: () => void;
      disable: () => void;
      status: () => boolean;
      diagnoseLinePlay: () => unknown;
    };
    /** Log transcript line play/pause blockers (always prints). */
    diagnoseTranscriptLinePlay?: () => unknown;
  }
}

if (typeof window !== 'undefined' && !window.sonnantChunkDebug) {
  window.sonnantChunkDebug = {
    enable() {
      localStorage.setItem(CHUNK_DEBUG_STORAGE_KEY, '1');
      // eslint-disable-next-line no-console
      console.info('[chunk-debug] enabled — reload to see logs');
    },
    disable() {
      localStorage.removeItem(CHUNK_DEBUG_STORAGE_KEY);
      // eslint-disable-next-line no-console
      console.info('[chunk-debug] disabled');
    },
    status: isChunkDebugEnabled,
    diagnoseLinePlay() {
      return (window as Window & { diagnoseTranscriptLinePlay?: () => unknown })
        .diagnoseTranscriptLinePlay?.();
    },
  };
}
